import React, { useState } from 'react';
import { SessionState, MaterializedView, Voicemail } from '@/types';
import { api } from '@/services/api';
import {
    Phone,
    PhoneForwarded,
    Voicemail as VoicemailIcon,
    PlayArrow,
    CheckCircle,
    ExpandMore,
    ExpandLess,
    Mail
} from '@mui/icons-material';

interface PhoneViewProps {
    session: SessionState;
    materializedView: MaterializedView;
    onReplyByEmail?: (callerNpcId: string) => void;
}

const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatTime = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const getSenderColor = (senderId: string): string => {
    const colors = [
        'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
        'bg-orange-600', 'bg-amber-600', 'bg-teal-600', 'bg-cyan-600',
    ];
    let hash = 0;
    for (let i = 0; i < senderId.length; i++) {
        hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const PhoneView: React.FC<PhoneViewProps> = ({ session, materializedView, onReplyByEmail }) => {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [listeningId, setListeningId] = useState<string | null>(null);

    const voicemails = materializedView.phone?.voicemails || [];
    const callHistory = materializedView.phone?.callHistory || [];
    const unlistenedCount = voicemails.filter(v => !v.isListened).length;

    const handleListen = async (voicemail: Voicemail) => {
        if (voicemail.isListened) {
            // Already listened — just toggle transcript
            setExpandedId(expandedId === voicemail.id ? null : voicemail.id);
            return;
        }

        setListeningId(voicemail.id);
        setExpandedId(voicemail.id);

        try {
            await api.listenVoicemail(session.id, voicemail.id);
        } catch (err) {
            console.error('Failed to mark voicemail as listened:', err);
        } finally {
            setListeningId(null);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-800 text-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-600">
                <div className="flex items-center gap-3">
                    <Phone className="text-blue-400" />
                    <div>
                        <h2 className="text-lg font-bold text-white">Phone</h2>
                        <p className="text-xs text-slate-300">
                            {unlistenedCount > 0
                                ? `${unlistenedCount} new voicemail${unlistenedCount > 1 ? 's' : ''}`
                                : 'All caught up'}
                        </p>
                    </div>
                    {unlistenedCount > 0 && (
                        <span className="ml-auto text-xs font-bold bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
                            {unlistenedCount}
                        </span>
                    )}
                </div>
            </div>

            {/* Voicemail list */}
            <div className="flex-1 overflow-y-auto">
                {voicemails.length === 0 && callHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <VoicemailIcon style={{ fontSize: '48px' }} className="mb-3" />
                        <p className="text-sm">No voicemails</p>
                    </div>
                ) : (
                    <>
                        {voicemails.length > 0 && (
                            <div className="divide-y divide-slate-600/50">
                                {voicemails.map((vm) => {
                                    const isExpanded = expandedId === vm.id;
                                    const isLoading = listeningId === vm.id;

                                    return (
                                        <div key={vm.id} className="group">
                                            <button
                                                onClick={() => handleListen(vm)}
                                                className="w-full text-left px-6 py-4 hover:bg-slate-900 transition-colors"
                                                disabled={isLoading}
                                            >
                                                <div className="flex items-center gap-4">
                                                    {/* Avatar */}
                                                    <div className="relative shrink-0">
                                                        <div className={`size-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${getSenderColor(vm.from)}`}>
                                                            {getInitials(vm.callerName)}
                                                        </div>
                                                        {!vm.isListened && (
                                                            <div className="absolute -top-0.5 -right-0.5 size-3 bg-red-500 rounded-full border-2 border-slate-800"></div>
                                                        )}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-sm font-bold ${vm.isListened ? 'text-slate-300' : 'text-white'}`}>
                                                                {vm.callerName}
                                                            </span>
                                                            {vm.isListened && (
                                                                <CheckCircle style={{ fontSize: '14px' }} className="text-blue-400/60" />
                                                            )}
                                                        </div>
                                                        <p className="text-[11px] text-slate-400">{vm.callerRole}</p>
                                                    </div>

                                                    {/* Meta */}
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <span className="text-[11px] text-slate-400 font-mono">
                                                            {formatDuration(vm.durationSeconds)}
                                                        </span>
                                                        <span className="text-[11px] text-slate-400">
                                                            {formatTime(vm.receivedAt)}
                                                        </span>
                                                        {!vm.isListened ? (
                                                            <PlayArrow fontSize="small" className="text-blue-400" />
                                                        ) : isExpanded ? (
                                                            <ExpandLess fontSize="small" className="text-slate-400" />
                                                        ) : (
                                                            <ExpandMore fontSize="small" className="text-slate-400" />
                                                        )}
                                                    </div>
                                                </div>
                                            </button>

                                            {/* Transcript */}
                                            {isExpanded && (
                                                <div className="px-6 pb-4">
                                                    <div className="ml-14 bg-slate-900 border border-slate-600 rounded-lg p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <VoicemailIcon style={{ fontSize: '14px' }} className="text-blue-400/60" />
                                                            <span className="text-[10px] text-blue-400/60 font-bold uppercase tracking-wider">
                                                                Transcript
                                                            </span>
                                                            {isLoading && (
                                                                <span className="text-[10px] text-amber-400 animate-pulse">
                                                                    Listening...
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-slate-200 leading-relaxed">
                                                            {vm.transcript}
                                                        </p>
                                                        {onReplyByEmail && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); onReplyByEmail(vm.from); }}
                                                                className="mt-3 flex items-center gap-2 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider"
                                                            >
                                                                <Mail fontSize="inherit" style={{ fontSize: '14px' }} /> Reply by Email
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Call History */}
                        {callHistory.length > 0 && (
                            <div className="border-t border-slate-600">
                                <div className="px-6 py-3 bg-slate-700/30">
                                    <div className="flex items-center gap-2">
                                        <PhoneForwarded style={{ fontSize: '16px' }} className="text-green-400/70" />
                                        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Call History
                                        </span>
                                        <span className="text-xs text-slate-400">({callHistory.length})</span>
                                    </div>
                                </div>
                                <div className="divide-y divide-slate-600/50">
                                    {callHistory.map((call) => (
                                        <div key={call.id} className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className={`size-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${getSenderColor(call.to)}`}>
                                                    {getInitials(call.toName)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-slate-300">
                                                            {call.toName}
                                                        </span>
                                                        <span className="text-[10px] text-green-400/70 font-medium">
                                                            Outgoing
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">
                                                        {call.summary}
                                                    </p>
                                                </div>
                                                <span className="text-[11px] text-slate-400 shrink-0">
                                                    {formatTime(call.timestamp)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PhoneView;
